import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoadingSpinner } from '../../components/ui';
import { useNotify, useAuthStore } from '../../store';
import { getApiBaseUrl } from '../../lib/apiBase';
import { sanitizeHubReturnPath } from '../../lib/hubReturnPath';

/**
 * Hub launch landing page: GET /auth/sso?token=...
 * Exchanges Hub JWT via POST /api/v1/sso/validate (same-origin Netlify proxy in production).
 * Honors optional redirect/returnPath; never bounces back to Hub after success.
 */
export default function AuthSsoPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const { setUser } = useAuthStore();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState('');
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const run = async () => {
      try {
        const token =
          searchParams.get('token') ||
          searchParams.get('accessToken') ||
          searchParams.get('access_token') ||
          searchParams.get('jwt') ||
          searchParams.get('launchToken');
        const error = searchParams.get('error');
        if (error) throw new Error(decodeURIComponent(error));
        if (!token) throw new Error('Missing Hub launch token');

        const payload = JSON.stringify({ token, accessToken: token });
        let response = await fetch('/api/v1/sso/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        });
        if (!response.ok) {
          const apiBase = getApiBaseUrl().replace(/\/api$/i, '');
          response = await fetch(`${apiBase}/api/v1/sso/validate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
          });
        }
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Platform Hub SSO validate failed');
        }

        const salesAccess = data.data.tokens.accessToken;
        const salesRefresh = data.data.tokens.refreshToken;
        const user = data.data.user;
        if (!user || !salesAccess || !salesRefresh) {
          throw new Error('Incomplete Sales session from Hub SSO');
        }

        localStorage.setItem('access_token', salesAccess);
        localStorage.setItem('refresh_token', salesRefresh);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);

        setStatus('success');
        notify.success('Welcome Back!', `Signed in via Tripnovac Platform Hub`);
        const next = sanitizeHubReturnPath(
          searchParams.get('redirect') || searchParams.get('returnPath') || '/dashboard',
          '/dashboard'
        );
        setTimeout(() => navigate(next, { replace: true }), 800);
      } catch (err) {
        setStatus('error');
        const message = err instanceof Error ? err.message : 'Authentication failed';
        setErrorMessage(message);
        notify.error('Hub sign-in failed', `${message}. You can still use email login.`);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    run();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-12 text-center">
        {status === 'processing' && (
          <>
            <div className="mb-6">
              <LoadingSpinner size="lg" variant="plane" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Connecting to Sales</h1>
            <p className="text-gray-600">Validating your Platform Hub session</p>
          </>
        )}
        {status === 'success' && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Connected</h1>
            <p className="text-gray-600">Opening your workspace…</p>
          </>
        )}
        {status === 'error' && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Could not connect</h1>
            <p className="text-gray-600 mb-4">{errorMessage}</p>
            <p className="text-sm text-gray-500">Redirecting to local login…</p>
          </>
        )}
      </div>
    </div>
  );
}
